import React, { Fragment, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import axios, { CancelToken } from "axios";
import { useAppSelector } from "hooks/redux";

import { GetPrinterAll } from "api/rpc/2024-04/facilityAdmin/facility/printer";

import { StatusCode } from "api/protocols";

import Checkbox from "components/form/checkbox/Checkbox";
import Icon from "components/icon/Icon";
import { faPrint } from "@fortawesome/pro-regular-svg-icons";
import Sheet from "components/sheet/Sheet";
import Spin from "components/spin/spin";

import { IPrinter } from "pages/secure/facility/Admin";
import Portal from "elements/Portal";

type PrintersModalProps = {
  open: boolean;
  onClose: (printer?: IPrinter) => void;
};

type PrinterModalState = {
  id: number;
  device_name?: string;
  title?: string;
};

/* Return ID of printer if found in localStorage */
function obtainStoredPrinter(facilityId: number, key: keyof PrinterModalState) {
  const printerJSON = localStorage.getItem(`printer_${facilityId}`);
  return printerJSON === null ? null : (JSON.parse(printerJSON)[key] as PrinterModalState[keyof PrinterModalState]);
}

export default function PrintersModal(props: PrintersModalProps) {
  const { t } = useTranslation();
  const { facilityStore } = useAppSelector(store => store);

  const [printers, setPrinters] = useState<{ printers: IPrinter[] }>({
    printers: undefined,
  });

  const [state, setState] = useState<PrinterModalState>({
    id: null,
    device_name: null,
    title: null,
  });

  const [refresh, setRefresh] = useState(false);

  const selectedPrinter = printers.printers?.filter(value => {
    if (value.id === obtainStoredPrinter(facilityStore.facility?.id, "id")) {
      return true;
    }
  });

  function updateSelectedPrinterState() {
    setState({
      id: Number(obtainStoredPrinter(facilityStore.facility?.id, "id")),
      device_name: obtainStoredPrinter(facilityStore.facility?.id, "device_name")?.toString(),
      title: obtainStoredPrinter(facilityStore.facility?.id, "title")?.toString(),
    });
  }

  useEffect(() => {
    const source = axios.CancelToken.source();

    if (props.open) {
      updateSelectedPrinterState();
      void loadPrinters(source.token);
      return;
    }

    return () => {
      source.cancel();
    };
  }, [props.open]);

  useEffect(() => {
    const source = axios.CancelToken.source();

    if (refresh) {
      updateSelectedPrinterState();
      void loadPrinters(source.token);
    }

    return () => {
      source.cancel();
    };
  }, [refresh]);

  useEffect(() => {
    if (facilityStore.facility?.id) {
      updateSelectedPrinterState();
    }
  }, [facilityStore.facility?.id]);

  async function loadPrinters(token: CancelToken) {
    if (printers.printers !== undefined) {
      setPrinters(prev => ({ ...prev, printers: undefined }));
    }

    const res = await GetPrinterAll({}, token ? false : true, token);

    if (res.status === StatusCode.OK && res.data) {
      setPrinters({
        printers: res.data,
      });

      refresh && setRefresh(false);

      return;
    }

    setPrinters(prev => ({
      ...prev,
      printers: [],
    }));

    refresh && setRefresh(false);
  }

  // Reset State of Modal
  function handleOnClose() {
    updateSelectedPrinterState();
    props.onClose();
  }

  function handlePrinterClick(printer: IPrinter) {
    setState({ id: printer.id });
    props.onClose(printer);
  }

  return (
    <Portal isMounted={props.open}>
      <Sheet
        size="small"
        closable
        title="Printers"
        open={props.open}
        cancelText={t("secure.facility.admin.022")}
        onCancel={() => handleOnClose()}
        okText={t("Refresh")}
        onOk={() => setRefresh(true)}
        okDisabled={!printers.printers} // disable 'OK' if loading
      >
        <div className="ui-checkbox-group">
          {!printers.printers ? (
            <div style={{ width: "100%", height: "55px" }}>
              <Spin />
            </div>
          ) : (
            <>
              {selectedPrinter &&
                selectedPrinter.map(printer => {
                  return (
                    <Fragment key={printer.id}>
                      <div>SELECTED PRINTER</div>
                      <div className="w-full pb-4 mb-2" style={{ borderBottom: "1px solid #498EC0" }}>
                        <div
                          key={printer.id}
                          onClick={() => handlePrinterClick(printer)}
                          className={`cursor-pointer ui-checkbox-group-item ${
                            state.id === printer.id && "ui-checkbox-group-item_selected"
                          }`}
                        >
                          <div className="ui-checkbox-group-item-content">
                            <div className="ui-checkbox-group-item-lead">
                              <Icon size="small" icon={faPrint} />
                            </div>
                            <div className="ui-checkbox-group-item-text">
                              <p className="text-sm text-medium text-gray-700">{printer.title}</p>
                            </div>
                          </div>
                          <Checkbox
                            size="medium"
                            value={printer.id}
                            checked={state.id === printer?.id}
                            onChange={() => setState({ id: printer.id })}
                          />
                        </div>
                      </div>
                    </Fragment>
                  );
                })}

              {printers.printers &&
                printers.printers.map(printer => {
                  return (
                    <Fragment key={String(printer.id)}>
                      <div
                        onClick={() => handlePrinterClick(printer)}
                        className={`cursor-pointer ui-checkbox-group-item ${
                          state.id === printer.id && "ui-checkbox-group-item_selected"
                        }`}
                      >
                        <div className="ui-checkbox-group-item-content">
                          <div className="ui-checkbox-group-item-lead">
                            <Icon size="small" icon={faPrint} />
                          </div>
                          <div className="ui-checkbox-group-item-text">
                            <p className="text-sm text-medium text-gray-700">{printer.title}</p>
                          </div>
                        </div>
                        <Checkbox size="medium" value={printer.id} />
                      </div>
                    </Fragment>
                  );
                })}
            </>
          )}
        </div>
      </Sheet>
    </Portal>
  );
}
